/**
 * The app's identity system. The questionnaire is organised under four
 * sustainable-development thematics; their colours (defined as `--theme-*`
 * tokens in globals.css) are the primary wayfinding device across the whole
 * app — nav, badges, charts, report bands.
 *
 * The backend keys each thematic with a `css_class` string that is one of the
 * four keys below (see questionnaire_definition_thematic.css_class), or, for a
 * thematic with a custom colour, a `thematic-custom-…` token under which the
 * server's stylesheet defines the `--theme-custom*` variables (see
 * ./palettes.tsx). Seeded/placeholder thematics may have an empty class, which
 * resolves to the neutral fallback.
 */
export type ThematicKey = "governance" | "economic" | "social" | "environment";

export const THEMATIC_KEYS: readonly ThematicKey[] = [
  "governance",
  "economic",
  "social",
  "environment",
] as const;

function isThematicKey(value: string): value is ThematicKey {
  return (THEMATIC_KEYS as readonly string[]).includes(value);
}

/**
 * Normalise a backend `css_class` to a {@link ThematicKey}, or `null` when it's
 * empty/unknown (placeholder thematics). Callers fall back to {@link NEUTRAL}.
 */
export function thematicKey(cssClass?: string | null): ThematicKey | null {
  const trimmed = cssClass?.trim().toLowerCase();
  return trimmed && isThematicKey(trimmed) ? trimmed : null;
}

/**
 * The class roles a themed surface needs. Every value is a *literal* Tailwind
 * class string so Tailwind's content scanner emits it — do NOT build these by
 * interpolating the key (e.g. `bg-theme-${key}`), which would be purged.
 */
export interface ThematicClasses {
  /** Solid theme fill (section headers, chart fills, the coloured chip body). */
  solid: string;
  /** Text/icon colour to render ON the solid fill. */
  onSolid: string;
  /** The theme colour as accessible text on the white app background. */
  ink: string;
  /** A pale tint of the theme, used as an icon/chip background behind `ink`. */
  softBg: string;
  /** Theme-coloured border. */
  border: string;
  /** Focus/selection ring in the theme colour. */
  ring: string;
}

export const THEMATIC: Record<ThematicKey, ThematicClasses> = {
  governance: {
    solid: "bg-theme-governance",
    onSolid: "text-theme-governance-foreground",
    ink: "text-theme-governance-ink",
    softBg: "bg-theme-governance-soft",
    border: "border-theme-governance",
    ring: "ring-theme-governance",
  },
  economic: {
    solid: "bg-theme-economic",
    onSolid: "text-theme-economic-foreground",
    ink: "text-theme-economic-ink",
    softBg: "bg-theme-economic-soft",
    border: "border-theme-economic",
    ring: "ring-theme-economic",
  },
  social: {
    solid: "bg-theme-social",
    onSolid: "text-theme-social-foreground",
    ink: "text-theme-social-ink",
    softBg: "bg-theme-social-soft",
    border: "border-theme-social",
    ring: "ring-theme-social",
  },
  environment: {
    solid: "bg-theme-environment",
    onSolid: "text-theme-environment-foreground",
    ink: "text-theme-environment-ink",
    softBg: "bg-theme-environment-soft",
    border: "border-theme-environment",
    ring: "ring-theme-environment",
  },
};

/** Neutral fallback for thematics with no recognised `css_class`. */
export const NEUTRAL: ThematicClasses = {
  solid: "bg-muted",
  onSolid: "text-foreground",
  ink: "text-muted-foreground",
  softBg: "bg-secondary",
  border: "border-border",
  ring: "ring-ring",
};

const CUSTOM_TOKEN = /^thematic-custom-[0-9a-f]{12}$/;

/** Whether a backend `css_class` is a custom colour's token. */
export function isCustomThematic(cssClass?: string | null): boolean {
  return CUSTOM_TOKEN.test(cssClass?.trim() ?? "");
}

/**
 * A custom colour's classes: the `custom` Tailwind colours, plus the token on
 * the same element so the variables they read are defined there.
 */
function customClasses(token: string): ThematicClasses {
  return {
    solid: `bg-theme-custom ${token}`,
    onSolid: `text-theme-custom-foreground ${token}`,
    ink: `text-theme-custom-ink ${token}`,
    softBg: `bg-theme-custom-soft ${token}`,
    border: `border-theme-custom ${token}`,
    ring: `ring-theme-custom ${token}`,
  };
}

/** Resolve the class set for a backend `css_class` (neutral when unknown). */
export function thematicClasses(cssClass?: string | null): ThematicClasses {
  const key = thematicKey(cssClass);
  if (key) return THEMATIC[key];
  return isCustomThematic(cssClass) ? customClasses(cssClass!.trim()) : NEUTRAL;
}

export { IssueIcon } from "./issue-icon";
export { ThematicPalettes, THEMATIC_PALETTES_KEY } from "./palettes";
export { issueIconClasses } from "./issue-icon-classes";
